import { fetcher } from "@/lib/api";
import type { AuthUser } from "@/features/auth/api";
import { getApiBaseUrl } from "@/lib/api-base-url";

export type ReferralStats = {
  referralCode: string;
  referralLink: string;
  referralCredit: number;
  /** BDT credited to referrer per qualifying enrollment (from referral settings). */
  studentRewardPerEnrollmentBDT?: number;
  totalReferrals: number;
  totalEarned: number;
  recentReferrals: Array<{
    referredUserName: string;
    /** `GR-PRT02`: the buyer's first letter and three stars. */
    maskedName?: string;
    itemTitle?: { en: string; bn?: string };
    rewardAmount: number;
    status?: "pending" | "settled" | "reversed";
    createdAt: string;
  }>;
  /**
   * `GR-PRT02` (ADR-0156): the readable code and link to share, whether it can
   * still be changed (once), and what a friend gets. Absent from an API before it.
   */
  personalCode?: string;
  personalLink?: string;
  canChangeCode?: boolean;
  studentReferralEnabled?: boolean;
  friendDiscountPercent?: number;
  friendDiscountCap?: number;
  firstPurchaseOnly?: boolean;
};

export type UpdateProfilePayload = {
  name?: string;
  bio?: string;
  phone?: string;
  institution?: string;
  jobTitle?: string;
  address?: string;
  city?: string;
  country?: string;
  /** `EX-IDN03`: ISO 3166-1 alpha-2; the API writes `country` from it. `null` clears it. */
  countryCode?: string | null;
  githubUsername?: string;
  codeforcesHandle?: string;
  linkedinUrl?: string;
  facebookUrl?: string;
  websiteUrl?: string;
  avatarImageId?: string;
};

export type ChangePasswordPayload = {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
};

export type ImageUploadResult = {
  id: string;
  url: string;
  filename: string;
  provider: string;
};

export async function updateProfile(
  payload: UpdateProfilePayload,
  token: string,
): Promise<{ user: AuthUser }> {
  const res = await fetcher<{ user: AuthUser }>("/v1/me/profile", {
    method: "PATCH",
    body: JSON.stringify(payload),
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.success) throw new Error((res as { error: { message: string } }).error?.message);
  return res.data;
}

export async function changePassword(
  payload: ChangePasswordPayload,
  token: string,
): Promise<void> {
  const res = await fetcher("/v1/me/password", {
    method: "PATCH",
    body: JSON.stringify(payload),
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.success) throw new Error((res as { error: { message: string } }).error?.message);
}

export async function getReferralStats(token: string): Promise<ReferralStats> {
  const res = await fetcher<ReferralStats>("/v1/me/referral-stats", {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.success) {
    throw new Error("Failed to load referral stats");
  }
  return res.data;
}

/** `GR-PRT02`: change the personal code, once. Refusals arrive as `ApiError` with their code. */
export async function changeReferralCode(
  code: string,
  token: string,
): Promise<{ personalCode: string; personalLink: string }> {
  const res = await fetcher<{ personalCode: string; personalLink: string }>("/v1/me/referral-code", {
    method: "PUT",
    body: JSON.stringify({ code }),
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.success) throw new Error("Failed to change the code");
  return res.data;
}

export async function uploadAvatar(
  file: File,
  token: string,
): Promise<ImageUploadResult> {
  const formData = new FormData();
  formData.append("file", file);

  const baseUrl = getApiBaseUrl();
  const res = await fetch(`${baseUrl}/v1/images`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    credentials: "include",
    body: formData,
  });

  const json = await res.json() as { success: boolean; data?: ImageUploadResult; error?: { message: string } };
  if (!json.success) throw new Error(json.error?.message ?? "Upload failed");
  return json.data!;
}

/**
 * `EX-PRF01` (ADR-0084): the one switch over the public profile's judge
 * activity and achievements. The bearer comes from the fetcher's own store.
 */
export type MyActivityProfile = { hidden: boolean; username: string | null };

export async function fetchMyActivityProfile(): Promise<MyActivityProfile> {
  const res = await fetcher<MyActivityProfile>("/v1/me/activity-profile");
  if (!res.success) throw new Error((res as { error: { message: string } }).error?.message);
  return res.data;
}

export async function setActivityProfileHidden(hidden: boolean): Promise<MyActivityProfile> {
  const res = await fetcher<MyActivityProfile>("/v1/me/activity-profile", {
    method: "PATCH",
    body: JSON.stringify({ hidden }),
  });
  if (!res.success) throw new Error((res as { error: { message: string } }).error?.message);
  return res.data;
}

/**
 * `EX-IDN02` (ADR-0088 decision 6): the person's own username. `username` is
 * the lowercase address, `usernameDisplay` the same name in their capitals.
 */
export type MyUsername = {
  username: string;
  usernameDisplay: string;
  /** They chose or confirmed it; `false` is the one made from their email. */
  usernameChosen: boolean;
  /** When the next change opens; `null` while a change is allowed now. */
  usernameNextChangeAt: string | null;
};

export type UsernameAvailability = {
  username: string;
  available: boolean;
  current: boolean;
  reason?: "invalid" | "reserved" | "taken";
};

export async function fetchMyUsername(): Promise<MyUsername> {
  const res = await fetcher<MyUsername>("/v1/me/username");
  if (!res.success) throw new Error((res as { error: { message: string } }).error?.message);
  return res.data;
}

export async function checkUsernameAvailability(username: string): Promise<UsernameAvailability> {
  const res = await fetcher<UsernameAvailability>(`/v1/me/username/availability?username=${encodeURIComponent(username)}`);
  if (!res.success) throw new Error((res as { error: { message: string } }).error?.message);
  return res.data;
}

/** Throws the API's `ApiError`: `USERNAME_TAKEN`, `USERNAME_CHANGE_TOO_SOON` (with `details.nextChangeAt`) or a 400. */
export async function changeMyUsername(username: string): Promise<MyUsername> {
  const res = await fetcher<MyUsername>("/v1/me/username", {
    method: "PATCH",
    body: JSON.stringify({ username }),
  });
  if (!res.success) throw new Error((res as { error: { message: string } }).error?.message);
  return res.data;
}

/**
 * `EX-IDN04` (ADR-0088): institution suggestions as the person types. The
 * API answers after two letters, the curated list first, then what other
 * people already typed.
 */
export const SELF_TAUGHT = "Self-taught";

export type InstitutionSuggestion = { name: string; short: string | null; source: "list" | "people" | "self" };

export async function fetchInstitutionSuggestions(q: string): Promise<InstitutionSuggestion[]> {
  const res = await fetcher<{ items: InstitutionSuggestion[] }>(`/v1/institutions/suggest?q=${encodeURIComponent(q)}`);
  if (!res.success) throw new Error((res as { error: { message: string } }).error?.message);
  return res.data.items;
}

/** `EX-IDN05`: the Welcome screen was saved or put off; it does not come back. */
export async function markOnboardingSeen(): Promise<void> {
  const res = await fetcher<{ welcomePending: false }>("/v1/me/onboarding/seen", { method: "POST" });
  if (!res.success) throw new Error((res as { error: { message: string } }).error?.message);
}

/**
 * `EX-SOC01` (ADR-0129, `docs/07` "Follow"): a person on a follow list or in
 * the mutual line. `rating` is `null` for somebody unrated.
 */
export type FollowPerson = {
  username: string;
  usernameDisplay: string;
  displayName: string;
  avatarImageId: string | null;
  rating: number | null;
};

/** The signed-in viewer's side of a profile. */
export type FollowState = {
  viewerIsOwner: boolean;
  following: boolean;
  followsYou: boolean;
  mutual: FollowPerson[];
};

export type FollowResult = { following: boolean; followers: number };

export async function fetchFollowState(username: string): Promise<FollowState> {
  const res = await fetcher<FollowState>(`/v1/users/${encodeURIComponent(username)}/follow`);
  if (!res.success) throw new Error((res as { error: { message: string } }).error?.message);
  return res.data;
}

/** Follow (`true`) or unfollow (`false`); both are idempotent and answer the new count. */
export async function setFollowing(username: string, follow: boolean): Promise<FollowResult> {
  const res = await fetcher<FollowResult>(`/v1/users/${encodeURIComponent(username)}/follow`, { method: follow ? "PUT" : "DELETE" });
  if (!res.success) throw new Error((res as { error: { message: string } }).error?.message);
  return res.data;
}

/** `EX-SOC01`: whether the person's follow lists show to others; the counts always do. */
export type MyFollowLists = { hidden: boolean; username: string | null };

export async function fetchMyFollowLists(): Promise<MyFollowLists> {
  const res = await fetcher<MyFollowLists>("/v1/me/follow-lists");
  if (!res.success) throw new Error((res as { error: { message: string } }).error?.message);
  return res.data;
}

export async function setFollowListsHidden(hidden: boolean): Promise<MyFollowLists> {
  const res = await fetcher<MyFollowLists>("/v1/me/follow-lists", { method: "PATCH", body: JSON.stringify({ hidden }) });
  if (!res.success) throw new Error((res as { error: { message: string } }).error?.message);
  return res.data;
}

/**
 * `EX-SOC02` (ADR-0129 decision 4, `docs/07` "Status"): one line, plain
 * text, at most 80 characters, no links. `expiresAt` `null` is "until it is
 * cleared"; a status past its `expiresAt` is never shown.
 */
export type ProfileStatus = { text: string; setAt: string; expiresAt: string | null };
export type StatusExpiry = "day" | "week" | "never";
export type StatusReportReason = "spam" | "offensive" | "other";
export type MyStatus = { status: ProfileStatus | null };

export const STATUS_MAX_CHARS = 80;

export async function fetchMyStatus(): Promise<MyStatus> {
  const res = await fetcher<MyStatus>("/v1/me/status");
  if (!res.success) throw new Error((res as { error: { message: string } }).error?.message);
  return res.data;
}

/** Throws the API's `ApiError`: `STATUS_HAS_LINK`, or a 400 for an empty or long line. */
export async function saveMyStatus(text: string, expires: StatusExpiry): Promise<MyStatus> {
  const res = await fetcher<MyStatus>("/v1/me/status", { method: "PUT", body: JSON.stringify({ text, expires }) });
  if (!res.success) throw new Error((res as { error: { message: string } }).error?.message);
  return res.data;
}

export async function clearMyStatus(): Promise<MyStatus> {
  const res = await fetcher<MyStatus>("/v1/me/status", { method: "DELETE" });
  if (!res.success) throw new Error((res as { error: { message: string } }).error?.message);
  return res.data;
}

/** Throws the API's `ApiError`: `STATUS_NOT_FOUND` once the status is gone. `counted` is false for a repeat. */
export async function reportStatus(username: string, reason: StatusReportReason): Promise<{ reported: true; counted: boolean }> {
  const res = await fetcher<{ reported: true; counted: boolean }>(`/v1/users/${encodeURIComponent(username)}/status/report`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
  if (!res.success) throw new Error((res as { error: { message: string } }).error?.message);
  return res.data;
}
